import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuBriefcase, LuCheck, LuListX, LuLoaderCircle, LuRotateCcw, LuTrash2 } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { useTranslation } from '../../../i18n/LanguageContext';
import { DetailItem } from '../../ProfilePage/components/ProfileUi';

/**
 * "İş Detayları": the job's full text, project, dates and status. Mark done /
 * not done; the restricted account can delete it, an admin can delete it with
 * every later job of the same title ("Bu ve Sonraki İşleri Sil").
 */
const JobDetailsDialog = ({ isOpen, job, employeeName, isRestrictedUser, isAdmin, isDeleting, isBuildingCascade, onToggleStatus, onDelete, onCascadeDelete, onClose }) => {
  const { t, language, locale } = useTranslation();
  const label = (key, vars) => t(`operations.job.${key}`, vars);
  const [toggling, setToggling] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Kapanış animasyonu sürerken son iş gösterilir
  const lastJob = useRef(job);
  if (job) lastJob.current = job;
  const shown = job || lastJob.current;

  const formatDay = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const toggle = async () => {
    setToggling(true);
    await onToggleStatus();
    setToggling(false);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => !toggling && !isDeleting && onClose()}
        size="md"
        title={label('title')}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30" size="xs">
            <LuBriefcase aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={shown && (
          <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
            {isRestrictedUser && (
              <Button variant="danger" onClick={() => setConfirmDelete(true)} disabled={isDeleting || toggling}>
                <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                {label('delete')}
              </Button>
            )}
            {isAdmin && (
              <Button variant="danger" onClick={onCascadeDelete} disabled={isBuildingCascade || toggling}>
                <LuListX aria-hidden="true" className="tw-h-4 tw-w-4" />
                {label('cascade')}
              </Button>
            )}
            <Button variant={shown.status ? 'secondary' : 'success'} onClick={toggle} disabled={toggling} className="max-sm:tw-w-full sm:tw-ml-auto">
              {toggling && <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />}
              {!toggling && (shown.status ? <LuRotateCcw aria-hidden="true" className="tw-h-4 tw-w-4" /> : <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" />)}
              {shown.status ? label('markOpen') : label('markDone')}
            </Button>
          </div>
        )}
      >
        {shown && (
          <dl className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            {/* Pencere başlığı tek satıra kesilir; iş metni burada tam görünür */}
            <DetailItem label={label('job')} wide>{shown.title || '—'}</DetailItem>
            {shown.projectName && <DetailItem label={label('project')} wide>{shown.projectName}</DetailItem>}
            <DetailItem label={label('status')}>
              <Badge tone={shown.status ? 'success' : 'warning'}>{t(`profilePage.calendar.jobStatus.${shown.status ? 'done' : 'ongoing'}`)}</Badge>
            </DetailItem>
            <DetailItem label={label('employee')}>{employeeName || label('unknown')}</DetailItem>
            <DetailItem label={label('start')}>{formatDay(shown.startDate)}</DetailItem>
            <DetailItem label={label('end')}>{formatDay(shown.endDate)}</DetailItem>
            {shown.time && <DetailItem label={label('time')}>{shown.time}</DetailItem>}
            {shown.description && <DetailItem label={label('description')} wide>{shown.description}</DetailItem>}
          </dl>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete}
        title={label('deleteTitle')}
        message={label('deleteMessage', { title: shown?.title || '' })}
        icon={LuTrash2}
        tone="danger"
        confirmLabel={label('delete')}
        cancelLabel={label('cancel')}
        busy={isDeleting}
        onConfirm={async () => {
          await onDelete();
          setConfirmDelete(false);
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

JobDetailsDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  job: PropTypes.object,
  employeeName: PropTypes.string,
  isRestrictedUser: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isDeleting: PropTypes.bool,
  isBuildingCascade: PropTypes.bool,
  onToggleStatus: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCascadeDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default JobDetailsDialog;
